import { Box, Card, Tooltip, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ComputerIcon from '@mui/icons-material/Computer';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { mindtimeWazuhEndpointsApi } from 'src/api/mindtime/wazuhEndpointsApi';
import { Table } from 'src/common';
import type {
  WazuhEndpointCriticalVulnerabilitiesData,
  WazuhEndpointCriticalVulnerabilityItem,
  WazuhEndpointFailedComplianceData,
  WazuhEndpointFailedComplianceItem,
  WazuhEndpointHealthData,
  WazuhEndpointMetadataData,
  WazuhEndpointSecurityScoreData,
  WazuhEndpointSecurityOverviewData,
  WazuhEndpointSnapshotData,
  WazuhEndpointSnapshotSeverityCounts,
} from 'src/types/mindtime/wazuhEndpoints';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import { EdrDateTime, formatEdrDateTime } from '../../shared/edrPrimitives';
import {
  ENDPOINT_SECURITY_OVERVIEW_LABELS,
  mapEndpointHealthPillars,
  mapEndpointSecurityOverviewCounts,
  toEndpointRisk,
} from '../mapWazuhEndpoint';
import {
  SecurityDashboardCard,
  SecuritySecurityScoreCard,
  SecuritySeverityCompareBarChart,
  securityDashboardSuccessColor,
} from '../../shared/securityDashboardCard';
import {
  useSecurityDashboardGrid,
  SECURITY_DASHBOARD_RAIL_WIDTH,
} from '../../shared/useSecurityDashboardGrid';
import type { EndpointHealthMetric } from './endpointDetailsTypes';
import { SeverityPill, toEventSeverity } from './shared/endpointTableTab';
import type { EndpointRisk, EndpointStatus } from '../types';

const COLOR_RED = '#E53E3E';
const COLOR_GREEN = securityDashboardSuccessColor;
const COLOR_ORANGE = '#DD6B20';
const DASHBOARD_TABLE_PAGE_SIZE = 5;

const STATUS_STYLES: Record<EndpointStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: '#0D9488', bg: '#CCFBF1' },
  disconnected: { label: 'Disconnected', color: '#B45309', bg: '#FEF3C7' },
  never_connected: { label: 'Never connected', color: '#475569', bg: '#F1F5F9' },
  offline: { label: 'Offline', color: '#DC2626', bg: '#FEE2E2' },
};

const RISK_STYLES: Record<EndpointRisk, { label: string; color: string }> = {
  low: { label: 'Low Risk', color: '#16A34A' },
  medium: { label: 'Medium Risk', color: '#CA8A04' },
  high: { label: 'High Risk', color: '#EA580C' },
  critical: { label: 'Critical Risk', color: '#DC2626' },
};

const HEALTH_TONE_COLORS: Record<EndpointHealthMetric['tone'], string> = {
  success: COLOR_GREEN,
  warning: COLOR_ORANGE,
  danger: COLOR_RED,
};

const Pill: FC<{ label: string; color: string; bg?: string }> = ({ label, color, bg }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1,
      py: 0.25,
      borderRadius: '4px',
      fontSize: 11,
      fontWeight: 600,
      color,
      backgroundColor: bg ?? 'transparent',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const identityCardSx = {
  backgroundColor: '#12B6F80D',
  border: '1px solid #12B6F833',
  borderRadius: '10px',
  boxShadow: 'none',
} as const;

const IdentityMetaRow: FC<{ label: string; value: string; isLast?: boolean }> = ({
  label,
  value,
  isLast = false,
}) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    style={{
      gap: 12,
      padding: '1px 0',
      borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
    }}
  >
    <Typography
      style={{
        fontSize: 11,
        color: '#94A3B8',
        textAlign: 'left',
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      style={{
        fontSize: 11,
        color: '#1E293B',
        textAlign: 'right',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const EndpointIdentityCard: FC<{
  hostname: string;
  lastSeen: string;
  os: string;
  statusLabel: string;
  statusColor: string;
  statusBg: string;
}> = ({ hostname, lastSeen, os, statusLabel, statusColor, statusBg }) => (
  <Card sx={identityCardSx} style={{ boxSizing: 'border-box' }}>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box display="flex" alignItems="space-between" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8, marginBottom: 4 }}>
        <Typography
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            color: '#1E293B',
          }}
        >
          {hostname}
        </Typography>
        <Pill label={statusLabel} color={statusColor} bg={statusBg} />
      </Box>
      <IdentityMetaRow label="Last Seen" value={lastSeen} />
      <IdentityMetaRow label="OS" value={os} isLast />
    </Box>
  </Card>
);

const getMetadataStatusStyle = (status?: string) => {
  const key = (status ?? '').trim().toLowerCase().replace(/-/g, '_');
  if (key === 'never_connected') return STATUS_STYLES.never_connected;
  if (key === 'disconnected' || key === 'inactive') return STATUS_STYLES.disconnected;
  if (key === 'offline') return STATUS_STYLES.offline;
  return STATUS_STYLES.active;
};

const formatSeveritySubtitle = (counts: WazuhEndpointSnapshotSeverityCounts | undefined): string => {
  if (!counts) return '—';
  return `Critical: ${counts.critical ?? 0} · High: ${counts.high ?? 0} · Medium: ${counts.medium ?? 0}`;
};

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    style={{ gap: 12, padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}
  >
    <Typography style={{ fontSize: 12, color: '#94A3B8', flexShrink: 0 }}>{label}</Typography>
    <Typography
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: '#1E293B',
        textAlign: 'right',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const HealthBar: FC<{ metric: EndpointHealthMetric }> = ({ metric }) => (
  <Box style={{ marginBottom: 12 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
      <Typography style={{ fontSize: 11, color: '#64748B' }}>{metric.label}</Typography>
      <Typography
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: HEALTH_TONE_COLORS[metric.tone],
        }}
      >
        {metric.percent}%
      </Typography>
    </Box>
    <Box
      style={{
        height: 6,
        borderRadius: 3,
        backgroundColor: '#F1F5F9',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          width: `${metric.percent}%`,
          height: '100%',
          backgroundColor: HEALTH_TONE_COLORS[metric.tone],
          borderRadius: 3,
        }}
      />
    </Box>
  </Box>
);

type EndpointDashboardTabProps = {
  systemWazuhAgentId: string;
  displayName?: string;
};

const formatImpactPercent = (value: number): string =>
  `${value > 0 ? '+' : ''}${value}%`;

const EMPTY_OVERVIEW_COUNTS = [0, 0, 0, 0];

const getOverviewBarCounts = (
  data: WazuhEndpointSecurityOverviewData | null,
  loading: boolean
): number[] => {
  if (loading || !data) return EMPTY_OVERVIEW_COUNTS;
  return mapEndpointSecurityOverviewCounts(data);
};

const loadEndpointResource = <T,>(
  isCancelled: () => boolean,
  request: Promise<T>,
  setData: (value: T | null) => void,
  setLoading: (value: boolean) => void,
  label: string
) => {
  setLoading(true);
  request
    .then((data) => {
      if (!isCancelled()) setData(data);
    })
    .catch((error) => {
      if (!isCancelled()) {
        logger(error, 'error', `EndpointDashboardTab.${label}`);
        setData(null);
      }
    })
    .finally(() => {
      if (!isCancelled()) setLoading(false);
    });
};

const getPagedTableMeta = (
  data: { items?: unknown[]; totalRecords?: number } | null
) => ({
  rows: (data?.items ?? []) as TableData[],
  total: data?.totalRecords ?? 0,
});

const EndpointDashboardTab: FC<EndpointDashboardTabProps> = ({
  systemWazuhAgentId,
  displayName = '',
}) => {
  const { isSmDown, isMdDown, mainWithRailColumns } = useSecurityDashboardGrid();
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [securityScoreLoading, setSecurityScoreLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [securityOverviewLoading, setSecurityOverviewLoading] = useState(false);
  const [failedComplianceLoading, setFailedComplianceLoading] = useState(false);
  const [failedCompliancePage, setFailedCompliancePage] = useState(0);
  const [criticalVulnsLoading, setCriticalVulnsLoading] = useState(false);
  const [criticalVulnsPage, setCriticalVulnsPage] = useState(0);
  const [snapshot, setSnapshot] = useState<WazuhEndpointSnapshotData | null>(null);
  const [metadata, setMetadata] = useState<WazuhEndpointMetadataData | null>(null);
  const [securityScore, setSecurityScore] = useState<WazuhEndpointSecurityScoreData | null>(null);
  const [health, setHealth] = useState<WazuhEndpointHealthData | null>(null);
  const [failedCompliance, setFailedCompliance] = useState<WazuhEndpointFailedComplianceData | null>(null);
  const [criticalVulns, setCriticalVulns] = useState<WazuhEndpointCriticalVulnerabilitiesData | null>(null);
  const [securityOverview, setSecurityOverview] = useState<{
    d7: WazuhEndpointSecurityOverviewData | null;
    d30: WazuhEndpointSecurityOverviewData | null;
  }>({ d7: null, d30: null });

  useEffect(() => {
    const id = systemWazuhAgentId.trim();
    if (!id) {
      setSnapshot(null);
      setMetadata(null);
      setSecurityScore(null);
      setHealth(null);
      setSnapshotLoading(false);
      setMetadataLoading(false);
      setSecurityScoreLoading(false);
      setHealthLoading(false);
      return undefined;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    loadEndpointResource(
      isCancelled,
      mindtimeWazuhEndpointsApi.getSnapshot(id),
      setSnapshot,
      setSnapshotLoading,
      'snapshot'
    );
    loadEndpointResource(
      isCancelled,
      mindtimeWazuhEndpointsApi.getMetadata(id),
      setMetadata,
      setMetadataLoading,
      'metadata'
    );
    loadEndpointResource(
      isCancelled,
      mindtimeWazuhEndpointsApi.getSecurityScore(id),
      setSecurityScore,
      setSecurityScoreLoading,
      'securityScore'
    );
    loadEndpointResource(
      isCancelled,
      mindtimeWazuhEndpointsApi.getHealth(id),
      setHealth,
      setHealthLoading,
      'health'
    );

    return () => {
      cancelled = true;
    };
  }, [systemWazuhAgentId]);

  useEffect(() => {
    const id = systemWazuhAgentId.trim();
    if (!id) {
      setSecurityOverview({ d7: null, d30: null });
      setSecurityOverviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    loadEndpointResource(
      () => cancelled,
      Promise.all([
        mindtimeWazuhEndpointsApi.getSecurityOverview(id, '7d'),
        mindtimeWazuhEndpointsApi.getSecurityOverview(id, '30d'),
      ]).then(([d7, d30]) => ({ d7, d30 })),
      setSecurityOverview,
      setSecurityOverviewLoading,
      'securityOverview'
    );

    return () => {
      cancelled = true;
    };
  }, [systemWazuhAgentId]);

  useEffect(() => {
    setFailedCompliancePage(0);
    setCriticalVulnsPage(0);
  }, [systemWazuhAgentId]);

  useEffect(() => {
    const id = systemWazuhAgentId.trim();
    if (!id) {
      setFailedCompliance(null);
      setFailedComplianceLoading(false);
      return undefined;
    }

    let cancelled = false;
    loadEndpointResource(
      () => cancelled,
      mindtimeWazuhEndpointsApi.getFailedCompliance(id, {
        page: failedCompliancePage,
        pageSize: DASHBOARD_TABLE_PAGE_SIZE,
      }),
      setFailedCompliance,
      setFailedComplianceLoading,
      'failedCompliance'
    );

    return () => {
      cancelled = true;
    };
  }, [systemWazuhAgentId, failedCompliancePage]);

  useEffect(() => {
    const id = systemWazuhAgentId.trim();
    if (!id) {
      setCriticalVulns(null);
      setCriticalVulnsLoading(false);
      return undefined;
    }

    let cancelled = false;
    loadEndpointResource(
      () => cancelled,
      mindtimeWazuhEndpointsApi.getCriticalVulnerabilities(id, {
        page: criticalVulnsPage,
        pageSize: DASHBOARD_TABLE_PAGE_SIZE,
      }),
      setCriticalVulns,
      setCriticalVulnsLoading,
      'criticalVulnerabilities'
    );

    return () => {
      cancelled = true;
    };
  }, [systemWazuhAgentId, criticalVulnsPage]);

  const activeAlertsTotal = snapshot?.alerts?.total;
  const vulnerabilitiesTotal = snapshot?.vulnerabilities?.total;
  const riskStyle = RISK_STYLES[toEndpointRisk(snapshot?.risk)];
  const updatedLabel = snapshot?.meta?.lastSyncedAt
    ? `Updated: ${new Date(snapshot.meta.lastSyncedAt).toLocaleString()}`
    : 'Updated: Just now';

  const identityStatus = metadata?.status
    ? getMetadataStatusStyle(metadata.status)
    : STATUS_STYLES.never_connected;
  const identityStatusLabel = identityStatus.label;
  const identityHostname = metadataLoading
    ? '—'
    : metadata?.hostname?.trim() || displayName || '—';
  const identityLastSeen = metadataLoading ? '—' : formatEdrDateTime(metadata?.lastSeen);
  const identityOs = metadataLoading ? '—' : metadata?.os?.trim() || '—';
  const summaryText = (value?: string | null) => (metadataLoading ? '—' : value?.trim() || '—');
  const summaryDate = (value?: string | null) => (metadataLoading ? '—' : formatEdrDateTime(value));

  const securityScoreValue = securityScoreLoading
    ? '—'
    : securityScore?.score != null
      ? `${securityScore.score.toFixed(1)}%`
      : '—';

  const securityScoreImpacts = useMemo(() => {
    if (securityScoreLoading) {
      return [{ id: 'loading', label: '—', impact: '—' }];
    }
    if (securityScore?.impacts?.length) {
      return securityScore.impacts.map((impact, index) => ({
        id: `impact-${index}`,
        label: `${impact.factor}:`,
        impact: formatImpactPercent(impact.impactPercent),
      }));
    }
    return [];
  }, [securityScore, securityScoreLoading]);

  const securityOverviewLast7Days = getOverviewBarCounts(
    securityOverview.d7,
    securityOverviewLoading
  );
  const securityOverviewLast30Days = getOverviewBarCounts(
    securityOverview.d30,
    securityOverviewLoading
  );

  const endpointHealthScore =
    health?.overallHealthScore != null ? Math.round(health.overallHealthScore) : 0;
  const endpointHealthMetrics = useMemo<EndpointHealthMetric[]>(
    () => mapEndpointHealthPillars(health?.pillars),
    [health?.pillars]
  );

  const portfolioHealthKpiColumns = isSmDown
    ? 'minmax(0, 1fr)'
    : isMdDown
      ? 'repeat(2, minmax(0, 1fr))'
      : 'repeat(3, minmax(0, 1fr))';

  const railWidth = `${SECURITY_DASHBOARD_RAIL_WIDTH}px`;

  const topRowColumns = isMdDown
    ? 'minmax(0, 1fr)'
    : `minmax(200px, 248px) minmax(0, 1fr) ${railWidth}`;

  const mainSubGridColumns = isMdDown
    ? 'minmax(0, 1fr)'
    : 'minmax(0, 1fr) minmax(0, 1.15fr)';

  const criticalVulnsColumns: Column[] = useMemo(
    () => [
      {
        id: 'vulnerability',
        label: 'Vulnerability',
        width: '60%',
        render: (row) => {
          const item = row as unknown as WazuhEndpointCriticalVulnerabilityItem;
          const title = item.title?.trim() || '—';
          const subtitle = [
            item.cveId?.trim() || '—',
            item.packageName?.trim() || '',
          ]
            .filter(Boolean)
            .join(' · ');
          const shortTitle = title.length > 50 ? `${title.slice(0, 50)}…` : title;
          const shortSubtitle =
            subtitle.length > 40 ? `${subtitle.slice(0, 40)}…` : subtitle;
          const tooltipTitle =
            title !== '—' || subtitle !== '—'
              ? `${title}${subtitle && subtitle !== '—' ? `\n${subtitle}` : ''}`
              : '';

          return (
            <Tooltip
              title={
                tooltipTitle ? (
                  <span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>
                ) : (
                  ''
                )
              }
              placement="top"
              enterDelay={300}
            >
              <Box
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <Typography
                  noWrap
                  component="div"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#1E293B',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {shortTitle}
                </Typography>
                <Typography
                  noWrap
                  component="div"
                  style={{
                    fontSize: 11,
                    color: '#94A3B8',
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {shortSubtitle}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        width: '20%',
        render: (row) => (
          <SeverityPill
            severity={toEventSeverity(
              (row as unknown as WazuhEndpointCriticalVulnerabilityItem).severity
            )}
          />
        ),
      },
      {
        id: 'detectedAt',
        label: 'Detected',
        align: 'right',
        width: '20%',
        render: (row) => (
          <div style={{ textAlign: 'right' }}>
            <EdrDateTime
              value={(row as unknown as WazuhEndpointCriticalVulnerabilityItem).detectedAt}
            />
          </div>
        ),
      },
    ],
    []
  );

  const failedComplianceColumns: Column[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Check',
        render: (row) => {
          const item = row as unknown as WazuhEndpointFailedComplianceItem;
          const frameworks = Array.isArray(item.framework)
            ? item.framework.filter(Boolean).join(' · ')
            : '';
          return (
            <Box>
              <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
                {item.title?.trim() || '—'}
              </Typography>
              <Typography style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                {item.checkId ? `ID ${item.checkId}` : '—'}
                {frameworks ? ` · ${frameworks}` : ''}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: 'category',
        label: 'Category',
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B' }}>
            {(row as unknown as WazuhEndpointFailedComplianceItem).category?.trim() || '—'}
          </Typography>
        ),
      },
      {
        id: 'lastChecked',
        label: 'Last Checked',
        align: 'right',
        render: (row) => (
          <div style={{ textAlign: 'right' }}>
            <EdrDateTime
              value={(row as unknown as WazuhEndpointFailedComplianceItem).lastChecked}
            />
          </div>
        ),
      },
    ],
    []
  );

  const criticalVulnsMeta = getPagedTableMeta(criticalVulns);
  const failedComplianceMeta = getPagedTableMeta(failedCompliance);

  const portfolioHealthKpis = (
    <SecurityDashboardCard
      title="Portfolio Health"
      icon={<FavoriteBorderIcon style={{ fontSize: 18 }} />}
      headerRight={
        <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>{updatedLabel}</Typography>
      }
      style={{ height: '100%', minWidth: 0 }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: portfolioHealthKpiColumns,
          py: 2,
          px: { xs: 1, sm: 0 },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            px: 2,
            borderTop: { xs: '1px solid #EDF2F7', md: 'none' },
            '&:first-of-type': { borderTop: 'none' },
            minWidth: 0,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}>
              Active Alerts
            </Typography>
            <NotificationsIcon style={{ fontSize: 16, color: COLOR_RED, flexShrink: 0 }} />
          </Box>
          <Typography style={{ fontSize: 22, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1 }}>
            {snapshotLoading || activeAlertsTotal === undefined
              ? '—'
              : String(activeAlertsTotal).padStart(2, '0')}
          </Typography>
          <Typography style={{ fontSize: 11, color: '#718096', marginTop: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {snapshotLoading ? '—' : formatSeveritySubtitle(snapshot?.alerts)}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            borderTop: { xs: '1px solid #EDF2F7', md: 'none' },
            '@media (min-width: 900px)': { borderLeft: '1px solid #EDF2F7' },
            minWidth: 0,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}>
              Vulnerabilities
            </Typography>
            <WarningIcon style={{ fontSize: 16, color: COLOR_RED, flexShrink: 0 }} />
          </Box>
          <Typography style={{ fontSize: 22, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1 }}>
            {snapshotLoading || vulnerabilitiesTotal === undefined ? '—' : String(vulnerabilitiesTotal)}
          </Typography>
          <Typography style={{ fontSize: 11, color: '#718096', marginTop: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {snapshotLoading ? '—' : formatSeveritySubtitle(snapshot?.vulnerabilities)}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            borderTop: { xs: '1px solid #EDF2F7', md: 'none' },
            '@media (min-width: 900px)': { borderLeft: '1px solid #EDF2F7' },
            minWidth: 0,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}>
              Risk Level
            </Typography>
            <BarChartIcon style={{ fontSize: 16, color: riskStyle.color, flexShrink: 0 }} />
          </Box>
          <Typography style={{ fontSize: 18, fontWeight: 700, color: riskStyle.color, lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {snapshotLoading ? '—' : riskStyle.label}
          </Typography>
        </Box>
      </Box>
    </SecurityDashboardCard>
  );

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {/* Top row: identity + portfolio health + security score */}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: topRowColumns,
          gap: 16,
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <EndpointIdentityCard
          hostname={identityHostname}
          lastSeen={identityLastSeen}
          os={identityOs}
          statusLabel={identityStatusLabel}
          statusColor={identityStatus.color}
          statusBg={identityStatus.bg}
        />
        {portfolioHealthKpis}
        <SecuritySecurityScoreCard
          icon={<ShowChartIcon style={{ fontSize: 18 }} />}
          score={securityScoreValue}
          comparison=""
          impactRows={securityScoreImpacts}
          style={{ height: '100%' }}
        />
      </Box>

      {/* Main content + right rail (avoids row-height coupling from 3-col grid) */}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: mainWithRailColumns,
          gap: 16,
          alignItems: 'start',
          minWidth: 0,
        }}
      >
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: mainSubGridColumns,
            gap: 16,
            alignItems: 'stretch',
            minWidth: 0,
          }}
        >
          <SecurityDashboardCard
            title="Critical Vulnerabilities"
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            overflow="hidden"
            style={{ height: '100%' }}
          >
            <Table
              columns={criticalVulnsColumns}
              data={criticalVulnsMeta.rows}
              loading={criticalVulnsLoading}
              showPagination
              compactPagination
              hideRowsPerPage
              page={criticalVulnsPage}
              rowsPerPage={DASHBOARD_TABLE_PAGE_SIZE}
              totalRows={criticalVulnsMeta.total}
              onPageChange={setCriticalVulnsPage}
              TableContainerProps={{
                sx: {
                  boxShadow: 'none',
                  borderRadius: 0,
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'hidden',
                },
              }}
              MUITableProps={{
                sx: {
                  tableLayout: 'fixed',
                  width: '100%',
                },
              }}
            />
          </SecurityDashboardCard>

          <SecurityDashboardCard
            title="Security Overview"
            icon={<ReportProblemIcon style={{ fontSize: 18 }} />}
            style={{ height: '100%' }}
          >
            <SecuritySeverityCompareBarChart
              last30Days={securityOverviewLast30Days}
              last7Days={securityOverviewLast7Days}
              labels={[...ENDPOINT_SECURITY_OVERVIEW_LABELS]}
              fill
            />
          </SecurityDashboardCard>

          <Box style={{ gridColumn: isMdDown ? undefined : '1 / -1', minWidth: 0 }}>
            <SecurityDashboardCard
              title="Failed Compliance"
              icon={<ErrorOutlineIcon style={{ fontSize: 18 }} />}
              overflow="hidden"
            >
              <Table
                columns={failedComplianceColumns}
                data={failedComplianceMeta.rows}
                loading={failedComplianceLoading}
                showPagination
                compactPagination
                hideRowsPerPage
                page={failedCompliancePage}
                rowsPerPage={DASHBOARD_TABLE_PAGE_SIZE}
                totalRows={failedComplianceMeta.total}
                onPageChange={setFailedCompliancePage}
                TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
              />
            </SecurityDashboardCard>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" style={{ gap: 16, minWidth: 0 }}>
          <SecurityDashboardCard title="Endpoint Summary" icon={<ComputerIcon style={{ fontSize: 18 }} />}>
            <Box sx={{ px: 2, py: 1 }}>
              <SummaryRow
                label="Hostname"
                value={summaryText(metadata?.hostname?.trim() || displayName)}
              />
              <SummaryRow label="Operating System" value={summaryText(metadata?.os)} />
              <SummaryRow label="IP Address" value={summaryText(metadata?.ip)} />
              <SummaryRow label="Agent Version" value={summaryText(metadata?.agentVersion)} />
              <SummaryRow label="First Seen" value={summaryDate(metadata?.dateAdd)} />
              <SummaryRow label="Last Seen" value={summaryDate(metadata?.lastSeen)} />
            </Box>
          </SecurityDashboardCard>

          <SecurityDashboardCard title="Endpoint Health" icon={<FavoriteBorderIcon style={{ fontSize: 18 }} />}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box display="flex" alignItems="baseline" style={{ gap: 8, marginBottom: 16 }}>
                <Typography style={{ fontSize: 28, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1 }}>
                  {healthLoading ? '—' : `${endpointHealthScore}%`}
                </Typography>
                <Typography style={{ fontSize: 12, fontWeight: 600, color: riskStyle.color }}>
                  {riskStyle.label}
                </Typography>
              </Box>
              {endpointHealthMetrics.map((metric) => (
                <HealthBar key={metric.id} metric={metric} />
              ))}
            </Box>
          </SecurityDashboardCard>
        </Box>
      </Box>
    </Box>
  );
};

export default EndpointDashboardTab;
